/**
 * Derived data: permissions (the protocol's shared pure functions), names,
 * role colours, the channel tree, voice members, unread state.
 */
import {
  ChannelType,
  compareRolesTopFirst,
  compareSnowflakes,
  computeBasePermissions,
  computeChannelPermissions,
  computeThreadPermissions,
  hasPermission,
  iconRoleOf,
  iconRolesForMode,
  isThreadChannelType,
  isVoiceChannelType,
  permissionThreadFromWire,
  memberTopRank,
  normalizeForSearch,
  permissionChannelFromWire,
  permissionContextFromWire,
  PRIVATE_CHANNEL_PERMISSIONS,
  RelationshipType,
  roleRank,
  type Channel,
  type ComputedPermissions,
  type Guild,
  type GuildMember,
  type PermissionContext,
  type PresenceStatus,
  type Role,
  type RoleIconMode,
  type Snowflake,
  type User,
  type VoiceState,
} from '@neosocial/protocol';
import { useMemo } from 'react';
import { tr } from '../i18n';
import { useData } from './data';
import { isUnread, type DataState } from './reducer';
import { useUi } from './ui';
import { sortVoiceStates } from './voiceOrder';
import { isMuted } from '../notify/rules';

/** The official community (`NX_OFFICIAL`); else the first server (an older server, tests). */
export function officialGuildId(s: DataState): Snowflake | null {
  for (const id of s.guildOrder) if (s.guilds[id]?.features?.includes(OFFICIAL)) return id;
  return s.guildOrder[0] ?? null;
}

const OFFICIAL = 'NX_OFFICIAL';

/**
 * The open server (Console v2: many servers): the one the rail selected
 * (ui `guildId`) while you are in it, else the official one.
 */
export function activeGuildId(
  s: DataState,
  open: Snowflake | null = useUi.getState().guildId,
): Snowflake | null {
  return open && s.guilds[open] ? open : officialGuildId(s);
}

/** `activeGuildId` as a hook: re-renders when the rail opens another server. */
export function useActiveGuildId(): Snowflake | null {
  const open = useUi((u) => u.guildId);
  return useData((d) => activeGuildId(d, open));
}

/**
 * The rail, top to bottom: the official server, then your order
 * (`user_settings.guild_positions`, dragged), then the rest by when you
 * joined (READY's order, then GUILD_CREATE's).
 */
export function railGuildIds(s: DataState): Snowflake[] {
  const official = officialGuildId(s);
  const out: Snowflake[] = official ? [official] : [];
  for (const id of s.settings?.guild_positions ?? [])
    if (s.guilds[id] && !out.includes(id)) out.push(id);
  for (const id of s.guildOrder) if (!out.includes(id)) out.push(id);
  return out;
}

/** No user has this ID: while viewing as a role, nobody is the owner. */
const NOBODY = '0';

/** The roles the signed-in user previews the guild with ("View server as role"), or null. */
export function viewAsRoles(s: DataState, guildId: Snowflake | null): Snowflake[] | null {
  return guildId && s.viewAs && s.viewAs.guildId === guildId ? s.viewAs.roleIds : null;
}

/**
 * The calculator's input, built from the wire objects by the protocol's
 * `permissionContextFromWire`, the same adapter the server's matrix test
 * feeds, so client and server agree (roles-permissions.md, C2). While the
 * signed-in user views the guild as a role, their own context uses those
 * roles and no ownership.
 */
export function permissionContext(
  s: DataState,
  guildId: Snowflake,
  userId?: Snowflake,
): PermissionContext | null {
  const g = s.guilds[guildId];
  const uid = userId ?? s.me?.id;
  if (!g || !uid) return null;
  const preview = uid === s.me?.id ? viewAsRoles(s, guildId) : null;
  if (preview)
    return permissionContextFromWire({ ...g, owner_id: NOBODY }, uid, { roles: preview });
  return permissionContextFromWire(g, uid, s.members[guildId]?.[uid]);
}

const NONE: ComputedPermissions = { permissions: 0n, nx_permissions: 0n };

/** Guild-level permissions of the signed-in user (or another user). */
export function basePermissions(
  s: DataState,
  guildId: Snowflake | null,
  userId?: Snowflake,
): ComputedPermissions {
  if (!guildId) return NONE;
  const ctx = permissionContext(s, guildId, userId);
  return ctx ? computeBasePermissions(ctx) : NONE;
}

/**
 * Channel permissions (Discord's algorithm, shared with the server). A DM or
 * group DM has the fixed set the server grants (no Manage Messages: you
 * delete only your own messages there).
 */
export function channelPermissions(
  s: DataState,
  channel: Channel | undefined,
  userId?: Snowflake,
): ComputedPermissions {
  if (!channel?.guild_id)
    return channel ? { permissions: PRIVATE_CHANNEL_PERMISSIONS, nx_permissions: 0n } : NONE;
  const ctx = permissionContext(s, channel.guild_id, userId);
  if (!ctx) return NONE;
  // a thread: its parent's permissions and Discord's thread rules (the server's own)
  if (isThreadChannelType(channel.type)) {
    const parent = channel.parent_id ? s.channels[channel.parent_id] : undefined;
    if (!parent) return NONE;
    const own = permissionThreadFromWire(channel);
    // another user's membership is not known here: theirs counts as not joined
    if (userId && userId !== s.me?.id) own.member = false;
    return computeThreadPermissions(ctx, permissionChannelFromWire(parent), own);
  }
  return computeChannelPermissions(ctx, permissionChannelFromWire(channel));
}

export function can(p: ComputedPermissions, flag: bigint): boolean {
  return hasPermission(p.permissions, flag);
}
export function canNx(p: ComputedPermissions, flag: bigint): boolean {
  return hasPermission(p.nx_permissions, flag);
}

/** The account was deleted for good: its old messages stay, its name does not. */
export function isDeletedUser(user: Pick<User, 'nx_deleted'> | null | undefined): boolean {
  return user?.nx_deleted === true;
}

/**
 * The name to show. A deleted account is "Deleted account" in this language
 * (Discord's "Deleted User"), never the stand-in handle the deletion gave it
 * (QA 2026-10-28: messages read "deleted.tt92sux5b0g").
 */
export function displayName(user: User | undefined, member?: GuildMember | null): string {
  if (!user) return '';
  if (isDeletedUser(user)) return tr().t('ns.deletedAccount');
  return member?.nick || user.global_name || user.username;
}

/**
 * A member search, Discord's: the server nickname, the display name and the
 * username all match (QA 2026-11-02: a thread's "Add people" found Cara but
 * not qa_cara). Persian letters and digits are normalised, a leading "@"
 * ignored; an empty query matches everyone.
 */
export function matchesMember(
  user: User | undefined,
  member: GuildMember | null | undefined,
  query: string,
): boolean {
  const q = normalizeForSearch(query.trim().replace(/^@/, ''));
  if (!q) return true;
  if (!user) return false;
  return [displayName(user, member), member?.nick, user.global_name, user.username].some(
    (n) => !!n && normalizeForSearch(n).includes(q),
  );
}

export function userName(s: DataState, userId: Snowflake, guildId?: Snowflake | null): string {
  const u = s.users[userId];
  const m = guildId ? s.members[guildId]?.[userId] : undefined;
  return displayName(u, m) || userId;
}

/** Roles top first, in Discord's order (the server's: position, then the older role). */
export function sortedRoles(g: Guild | undefined): Role[] {
  if (!g) return [];
  return g.roles.slice().sort(compareRolesTopFirst);
}

/** The member's highest role that passes `ok` (Discord's order). */
function topRoleWhere(
  g: Guild | undefined,
  m: GuildMember | undefined,
  ok: (r: Role) => boolean,
): Role | null {
  if (!g || !m) return null;
  let best: Role | null = null;
  for (const r of g.roles) {
    if (!m.roles.includes(r.id) || !ok(r)) continue;
    if (!best || compareRolesTopFirst(r, best) < 0) best = r;
  }
  return best;
}

/** The member's highest role that has a colour. */
export function colorRole(g: Guild | undefined, m: GuildMember | undefined): Role | null {
  return topRoleWhere(g, m, (r) => !!r.color);
}

/** The member's highest hoisted role (member list groups). */
export function hoistRole(g: Guild | undefined, m: GuildMember | undefined): Role | null {
  return topRoleWhere(g, m, (r) => r.hoist);
}

/** The role whose icon the member shows: their highest role with an image or an emoji. */
export function iconRole(g: Guild | undefined, m: GuildMember | undefined): Role | null {
  if (!g || !m) return null;
  return iconRoleOf(g.roles, m.roles);
}

const NO_ROLES: Role[] = [];

/*
 * The selectors below run on every data-store update (typing, presence,
 * messages) for every voice row, member list row and message header, so
 * they reuse what they worked out last time. The store never edits a role
 * list or a member's role IDs in place (the reducer swaps in new arrays),
 * so those arrays are safe cache keys.
 */

/** Per member role ID list: `iconRoles`' answers for the guild role list they came from. */
const iconRolesMemo = new WeakMap<
  readonly Snowflake[],
  { roles: readonly Role[]; highest?: Role[]; all?: Role[] }
>();
/** Per guild role list: the IDs of the roles that have an icon (an image or an emoji). */
const iconIdSets = new WeakMap<readonly Role[], ReadonlySet<Snowflake>>();

function iconRoleIds(roles: readonly Role[]): ReadonlySet<Snowflake> {
  let ids = iconIdSets.get(roles);
  if (!ids) {
    ids = new Set(roles.filter((r) => !!r.icon || !!r.unicode_emoji).map((r) => r.id));
    iconIdSets.set(roles, ids);
  }
  return ids;
}

/**
 * The roles whose icons the member shows in `mode` (the console's
 * `display.role_icons`), top first: at most one in `highest` mode; every
 * icon role in `all` mode (drawn up to `ROLE_ICONS_SHOWN_MAX`, then "+N").
 * Items are the store's own role objects, and the same inputs give the
 * same array, so a store update that changed neither sorts nothing again.
 */
export function iconRoles(
  g: Guild | undefined,
  m: GuildMember | undefined,
  mode: RoleIconMode,
): Role[] {
  if (!g || !m) return NO_ROLES;
  const key = mode === 'all' ? 'all' : 'highest';
  let memo = iconRolesMemo.get(m.roles);
  if (!memo || memo.roles !== g.roles) iconRolesMemo.set(m.roles, (memo = { roles: g.roles }));
  let out = memo[key];
  if (!out) {
    const found = iconRolesForMode(g.roles, m.roles, key);
    out = memo[key] = found.length ? found : NO_ROLES;
  }
  return out;
}

/**
 * How many of the member's roles have an icon (what `iconRoles` gives in
 * `all` mode, counted): no filter, no sort, no array.
 */
export function iconRoleCount(g: Guild | undefined, m: GuildMember | undefined): number {
  if (!g || !m) return 0;
  const ids = iconRoleIds(g.roles);
  if (!ids.size) return 0;
  let n = 0;
  for (const id of m.roles) if (ids.has(id)) n++;
  return n;
}

/**
 * The widest role icon group among the people in a voice channel, as one
 * number (a primitive, so the channel's row re-renders only when it
 * changes): in `highest` mode 1 when anyone shows an icon, else 0, stopping
 * at the first who does (the rows are sorted by role, so usually the first
 * row); in `all` mode the most icon roles any one of them holds, which
 * gives both the icon columns (up to `ROLE_ICONS_SHOWN_MAX`) and the
 * widest "+N" chip.
 */
export function voiceIconMax(
  s: DataState,
  guildId: Snowflake,
  states: readonly VoiceState[],
  mode: RoleIconMode,
): number {
  const g = s.guilds[guildId];
  const members = s.members[guildId];
  if (!g || !members) return 0;
  if (mode !== 'all') return states.some((vs) => !!iconRole(g, members[vs.user_id])) ? 1 : 0;
  let max = 0;
  for (const vs of states) {
    const n = iconRoleCount(g, members[vs.user_id]);
    if (n > max) max = n;
  }
  return max;
}

/* ---------- role colours: the OKLCH contrast clamp (visual-overhaul.md C9, §9) ---------- */

/**
 * The surfaces a role-coloured name can sit on, per theme (packages/ui
 * tokens.css: --surface-1, --surface-2, --surface-raised; the member list,
 * the conversation, and the member drawer under 1100px). The surfaces are
 * glass (owner, 2026-10-29), so these are what they show over --bg-app (raised
 * over --surface-2). A test keeps these in step with the tokens.
 */
export const ROLE_SURFACES = {
  light: ['#f5f6fa', '#f8fafc'],
  dark: ['#0e1119', '#12161f', '#1d222f'],
} as const;
/** WCAG AA for body text. */
export const ROLE_MIN_CONTRAST = 4.5;
/** Chroma above this glows on the dark surfaces and vibrates on the light ones. */
const ROLE_MAX_CHROMA = 0.17;

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const fromLinear = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

/** An sRGB hex (#rrggbb) or integer as linear RGB, 0-1. */
function linearRgb(color: string | number): [number, number, number] {
  const n = typeof color === 'number' ? color : parseInt(color.slice(1), 16);
  return [
    toLinear(((n >> 16) & 255) / 255),
    toLinear(((n >> 8) & 255) / 255),
    toLinear((n & 255) / 255),
  ];
}

/** WCAG relative luminance of an sRGB hex or integer. */
export function luminance(color: string | number): number {
  const [r, g, b] = linearRgb(color);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio of two sRGB colours. */
export function contrastRatio(a: string | number, b: string | number): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** An sRGB hex or integer in OKLCH: [L 0-1, C, h in degrees]. */
export function toOklch(color: string | number): [number, number, number] {
  const [r, g, b] = linearRgb(color);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const h = (Math.atan2(B, A) * 180) / Math.PI;
  return [L, Math.hypot(A, B), h < 0 ? h + 360 : h];
}

function oklchToLinear(L: number, C: number, h: number): [number, number, number] {
  const a = C * Math.cos((h * Math.PI) / 180);
  const b = C * Math.sin((h * Math.PI) / 180);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/**
 * OKLCH as an sRGB hex. A colour outside sRGB keeps its lightness and hue
 * and loses chroma until it fits (no channel clipping, which would shift
 * the hue).
 */
export function oklchHex(L: number, C: number, h: number): string {
  const fits = (c: number) => oklchToLinear(L, c, h).every((v) => v >= -1e-5 && v <= 1 + 1e-5);
  let c = C;
  if (!fits(c)) {
    let lo = 0;
    let hi = C;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (fits(mid)) lo = mid;
      else hi = mid;
    }
    c = lo;
  }
  const hex = (v: number) =>
    Math.round(Math.min(1, Math.max(0, fromLinear(Math.min(1, Math.max(0, v))))) * 255)
      .toString(16)
      .padStart(2, '0');
  return '#' + oklchToLinear(L, c, h).map(hex).join('');
}

/**
 * A role's colour for one theme: the hue kept, chroma at most .17, and the
 * lightness moved (down on Pearl, up on Smoked) only as far as it takes to
 * reach 4.5:1 on every surface the name can sit on.
 */
export function clampRoleColor(color: number, theme: 'light' | 'dark'): string {
  const [L0, C0, h0] = toOklch(color);
  const C = Math.min(C0, ROLE_MAX_CHROMA);
  const h = C0 < 1e-4 ? 0 : h0;
  const surfaces = ROLE_SURFACES[theme];
  const ok = (L: number) => {
    const hex = oklchHex(L, C, h);
    return surfaces.every((s) => contrastRatio(hex, s) >= ROLE_MIN_CONTRAST);
  };
  if (ok(L0)) return oklchHex(L0, C, h);
  // Pearl: the lightest L that passes, below L0; Smoked: the darkest, above it
  let pass = theme === 'light' ? 0 : 1;
  let fail = L0;
  for (let i = 0; i < 24; i++) {
    const mid = (pass + fail) / 2;
    if (ok(mid)) pass = mid;
    else fail = mid;
  }
  return oklchHex(pass, C, h);
}

const rolePairs = new Map<number, [string, string]>();

/**
 * An integer RGB as a [pearl, smoked] pair for `.nx-rc`: each clamped to
 * 4.5:1 on its theme's surfaces (C9), cached per colour.
 */
export function roleColorPair(color: number): [string, string] {
  let pair = rolePairs.get(color);
  if (!pair) {
    pair = [clampRoleColor(color, 'light'), clampRoleColor(color, 'dark')];
    rolePairs.set(color, pair);
  }
  return pair;
}

export function presenceOf(s: DataState, userId: Snowflake): PresenceStatus {
  if (s.me?.id === userId) {
    const st = s.settings?.status ?? s.presences[userId]?.status ?? 'online';
    return st === 'invisible' ? 'offline' : (st as PresenceStatus);
  }
  return s.presences[userId]?.status ?? 'offline';
}

/**
 * A person's custom status (the text they set in Settings > Profile), shown
 * wherever their presence shows; null when they have none or look offline.
 */
export function customStatusOf(s: DataState, userId: Snowflake): string | null {
  if (presenceOf(s, userId) === 'offline') return null;
  if (userId === s.me?.id) return s.settings?.custom_status?.text || null;
  return s.presences[userId]?.activities?.find((a) => a.type === 4)?.state || null;
}

export interface CategoryNode {
  category: Channel | null;
  channels: Channel[];
}

const isCategory = (c: Channel) => c.type === ChannelType.GUILD_CATEGORY;
const isVoice = (c: Channel) => isVoiceChannelType(c.type);

/**
 * The icon a channel is drawn with, by its type: a category's list, the
 * speaker for every voice channel (voice and stage, as the sidebar draws
 * them), else '#'. QA 2026-10-28 follow-up round 3: Server settings › Manage
 * channels gave the speaker to GUILD_VOICE only, so the stage channel
 * "Friday Talk" had a text channel's '#'.
 */
export function channelIcon(type: number | undefined): 'list' | 'volume' | 'hash' {
  if (type === ChannelType.GUILD_CATEGORY) return 'list';
  return type !== undefined && isVoiceChannelType(type) ? 'volume' : 'hash';
}
const byPos = (a: Channel, b: Channel) =>
  (a.position ?? 0) - (b.position ?? 0) || compareSnowflakes(a.id, b.id);

/** The channel tree, recomputed only when the channels change. */
export function useChannelTree(guildId: Snowflake | null): CategoryNode[] {
  const channels = useData((d) => d.channels);
  return useMemo(() => channelTree({ channels }, guildId), [channels, guildId]);
}

/** Discord's sidebar order: uncategorised first, then categories; text before voice inside each. */
export function channelTree(
  s: Pick<DataState, 'channels'>,
  guildId: Snowflake | null,
): CategoryNode[] {
  if (!guildId) return [];
  const all = Object.values(s.channels).filter((c) => c.guild_id === guildId);
  const cats = all.filter(isCategory).sort(byPos);
  const childrenOf = (pid: Snowflake | null) =>
    all
      .filter((c) => !isCategory(c) && (c.parent_id ?? null) === pid)
      .sort((a, b) => Number(isVoice(a)) - Number(isVoice(b)) || byPos(a, b));
  const out: CategoryNode[] = [];
  const loose = childrenOf(null);
  if (loose.length) out.push({ category: null, channels: loose });
  for (const c of cats) out.push({ category: c, channels: childrenOf(c.id) });
  return out;
}

/**
 * A guild's channels (never a category) in the sidebar's order
 * (`channelTree`): every picker that lists channels uses it. QA 2026-10-28:
 * the invite form sorted by each channel's position, which counts inside
 * its own category, so channels of different categories mixed.
 */
export function channelsInSidebarOrder(
  s: Pick<DataState, 'channels'>,
  guildId: Snowflake | null,
): Channel[] {
  return channelTree(s, guildId).flatMap((n) => n.channels);
}

/** A guild's categories in the sidebar's order. */
export function categoriesInOrder(
  s: Pick<DataState, 'channels'>,
  guildId: Snowflake | null,
): Channel[] {
  return Object.values(s.channels)
    .filter((c) => c.guild_id === guildId && isCategory(c))
    .sort(byPos);
}

/**
 * The people in a voice channel, in the order every view shows them: the
 * highest role first (as ordered in Server settings), then by name for the
 * UI language (English; names in any script), then by ID (store/voiceOrder.ts).
 */
export function voiceStatesIn(
  s: DataState,
  guildId: Snowflake | null,
  channelId: Snowflake,
  lang = 'en',
): VoiceState[] {
  if (!guildId) return [];
  const here = Object.values(s.voiceStates[guildId] ?? {}).filter(
    (v) => v.channel_id === channelId,
  );
  return sortVoiceStates(here, {
    guild: s.guilds[guildId],
    members: s.members[guildId],
    users: s.users,
    lang,
  });
}

/** Your own voice state, in whichever server you are in voice (one at a time). */
export function myVoiceState(s: DataState): VoiceState | null {
  if (!s.me) return null;
  for (const g of s.guildOrder) {
    const v = s.voiceStates[g]?.[s.me.id];
    if (v) return v;
  }
  return null;
}

/**
 * A channel's unread state. A muted channel (itself, its category or the
 * server; notify/rules.ts) shows no unread dot, but its mentions still count
 * (Discord: "unless you are mentioned").
 */
export function unreadOf(
  s: DataState,
  channelId: Snowflake,
): { unread: boolean; mentions: number; muted: boolean } {
  const muted = isMuted(s, s.channels[channelId]);
  return {
    unread: !muted && isUnread(s, channelId),
    mentions: s.readStates[channelId]?.mention_count ?? 0,
    muted,
  };
}

/** The server's unread dot and mention badge: muted channels never light the dot. */
export function guildUnread(
  s: DataState,
  guildId: Snowflake | null,
): { unread: number; mentions: number } {
  let unread = 0;
  let mentions = 0;
  for (const c of Object.values(s.channels)) {
    if (c.guild_id !== guildId || isCategory(c) || isVoice(c)) continue;
    // a thread counts only once joined (Discord)
    if (isThreadChannelType(c.type) && !c.member) continue;
    if (isUnread(s, c.id) && !isMuted(s, c)) unread += 1;
    mentions += s.readStates[c.id]?.mention_count ?? 0;
  }
  return { unread, mentions };
}

/** DMs and group DMs, newest first. Message requests stay out until accepted. */
export function privateChannels(s: DataState): Channel[] {
  return Object.values(s.channels)
    .filter((c) => c.type === ChannelType.DM || c.type === ChannelType.GROUP_DM)
    .filter((c) => !c.nx_request_state || c.nx_request_state === 'accepted')
    .sort((a, b) => compareSnowflakes(b.last_message_id ?? b.id, a.last_message_id ?? a.id));
}

/**
 * The member's hierarchy rank: the protocol's `memberTopRank`, exactly what
 * the server checks (position, older role winning ties; 0 for none; the
 * owner above everyone). The signed-in user's rank follows "View server as
 * role" while it is on.
 */
export function topRolePosition(s: DataState, guildId: Snowflake, userId: Snowflake): number {
  const g = s.guilds[guildId];
  if (!g) return 0;
  const preview = userId === s.me?.id ? viewAsRoles(s, guildId) : null;
  if (preview) return memberTopRank({ id: g.id, owner_id: NOBODY }, g.roles, userId, preview);
  return memberTopRank(g, g.roles, userId, s.members[guildId]?.[userId]?.roles ?? []);
}

/** True when the signed-in user may manage `role` (owner, or a rank strictly above it). */
export function canManageRole(s: DataState, guildId: Snowflake, role: Role): boolean {
  const me = s.me?.id;
  const g = s.guilds[guildId];
  if (!me || !g) return false;
  if (role.id === guildId) return true; // @everyone sits below everyone
  const mine = topRolePosition(s, guildId, me);
  return mine === Number.POSITIVE_INFINITY || roleRankIn(g, role) < mine;
}

/** A role's rank in its guild (the protocol's `roleRank`). */
export function roleRankIn(g: Guild, role: Role): number {
  return roleRank(g.roles, role, g.id);
}

/**
 * The Home badge: unread DMs and their mentions. A DM with someone you
 * ignore (`ignored`, store/um.ts) is silent and never counts (§3.2).
 */
export function dmUnread(s: DataState, ignored: Record<Snowflake, true> = {}): number {
  let n = 0;
  for (const c of privateChannels(s)) {
    const peer = c.type === ChannelType.DM ? c.recipients?.find((u) => u.id !== s.me?.id) : null;
    if (peer && ignored[peer.id]) continue;
    n += s.readStates[c.id]?.mention_count ?? (isUnread(s, c.id) ? 1 : 0);
  }
  return n;
}

/**
 * Friend requests waiting for your answer (relationship type 3), for the
 * Home button's badge beside unread DMs (Discord counts them there too).
 */
export function pendingIncoming(s: DataState): number {
  let n = 0;
  for (const r of Object.values(s.relationships))
    if (r.type === RelationshipType.PENDING_INCOMING) n++;
  return n;
}

/** The name a DM shows: group name, else the other people, joined with the list separator. */
export function privateChannelName(s: DataState, c: Channel): string {
  if (c.name) return c.name;
  const others = (c.recipients ?? []).filter((u) => u.id !== s.me?.id);
  return (
    others.map((u) => displayName(s.users[u.id] ?? u)).join(tr().t('ns.listSep')) ||
    displayName(s.me ?? undefined)
  );
}

export { isVoice as isVoiceChannel, isCategory as isCategoryChannel };
